import React, { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChevronRightIcon } from "lucide-react";

interface Question {
  id: string;
  question: string;
  type: "text" | "choice" | "multiChoice";
  options?: string[];
  required?: boolean;
  placeholder?: string;
}

const NumberBadge = ({
  number,
  isSelected,
}: {
  number: number;
  isSelected: boolean;
}) => (
  <div
    className={cn(
      "w-5 h-5 mr-2.5 flex items-center justify-center shrink-0 transition-colors rounded text-[10px] font-semibold border",
      isSelected
        ? "bg-primary-0 text-white border-primary-0"
        : "bg-secondary-n-800 text-text-secondary border-[#2F3035]",
    )}
  >
    {number}
  </div>
);

const OptionCard = ({
  option,
  isSelected,
  onClick,
  disabled,
  number,
}: {
  option: string;
  isSelected: boolean;
  onClick: () => void;
  disabled: boolean;
  number: number;
}) => (
  <div
    data-testid={`question-option-${number}`}
    onClick={disabled ? undefined : onClick}
    className={cn(
      "flex items-center px-2.5 py-2 rounded-lg border cursor-pointer transition-all",
      "bg-secondary-n-1000 border-[#2F3035]",
      !disabled && "hover:border-hover",
      isSelected && "border-primary-0 bg-primary-0/10",
      disabled && "opacity-60 cursor-not-allowed",
    )}
  >
    <NumberBadge number={number} isSelected={isSelected} />
    <span className="text-xs text-text-primary">{option}</span>
  </div>
);

const EnterHint = ({ show }: { show: boolean }) =>
  show ? <span className="text-text-muted ml-2 text-sm">↵</span> : null;

const CustomTextInput = ({
  number,
  isCustomAnswer,
  isInputDisabled,
  value,
  onChange,
  onEnterKey,
}: {
  number: number;
  isCustomAnswer: boolean;
  isInputDisabled: boolean;
  value: string;
  onChange: (value: string) => void;
  onEnterKey?: () => void;
}) => (
  <div
    data-testid="question-custom-option"
    className={cn(
      "flex items-center px-2.5 py-2 rounded-lg border transition-all",
      "bg-secondary-n-1000 border-[#2F3035]",
      isCustomAnswer && "border-primary-0 bg-primary-0/10",
      isInputDisabled && "opacity-60 cursor-not-allowed",
    )}
  >
    <NumberBadge number={number} isSelected={isCustomAnswer} />
    <input
      data-testid="question-custom-input"
      type="text"
      placeholder="Type something else..."
      onKeyDown={(e) => {
        if (e.key === "Enter" && value.trim() && onEnterKey) {
          e.preventDefault();
          onEnterKey();
        }
      }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={isInputDisabled}
      className={cn(
        "flex-1 bg-transparent text-xs outline-hidden",
        "text-text-primary placeholder:text-text-muted",
      )}
    />
    <EnterHint show={!!value.trim() && !isInputDisabled} />
  </div>
);

interface UserAnswer {
  questionId: string;
  answer: string | string[];
}

interface AskUserQuestionsBlockProps {
  context: string;
  questions: Question[];
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  onSubmit?: (answers: UserAnswer[], skipped: boolean) => void;
  isSubmitting?: boolean;
  canSubmit?: boolean;
  submittedAnswers?: UserAnswer[];
  wasSkipped?: boolean;
}

export const AskUserQuestionsBlock: React.FC<AskUserQuestionsBlockProps> = ({
  context,
  questions,
  status,
  onSubmit,
  isSubmitting = false,
  canSubmit = false,
  submittedAnswers,
  wasSkipped = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [customText, setCustomText] = useState<Record<string, string>>({});

  useEffect(() => {
    if (submittedAnswers?.length) {
      const newAnswers = submittedAnswers.reduce(
        (acc, { questionId, answer }) => {
          acc[questionId] = answer;
          return acc;
        },
        {} as Record<string, string | string[]>,
      );
      setAnswers(newAnswers);
    }
  }, [submittedAnswers]);

  const currentQuestion = questions[currentStep];
  const totalSteps = questions.length;
  const isLastStep = currentStep === totalSteps - 1;
  const isInputDisabled = !canSubmit || isSubmitting;
  const hasSubmittedAnswers = submittedAnswers && submittedAnswers.length > 0;
  const isNavigationDisabled = isSubmitting;

  const handleTextChange = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleChoiceChange = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleMultiChoiceChange = (option: string) => {
    setAnswers((prev) => {
      const currentAnswers = (prev[currentQuestion.id] as string[]) || [];
      if (currentAnswers.includes(option)) {
        return {
          ...prev,
          [currentQuestion.id]: currentAnswers.filter((a) => a !== option),
        };
      }
      return { ...prev, [currentQuestion.id]: [...currentAnswers, option] };
    });
  };

  const handleCustomTextChange = (value: string) => {
    const prevCustomText = customText[currentQuestion.id] || "";
    setCustomText((prev) => ({ ...prev, [currentQuestion.id]: value }));

    if (currentQuestion.type === "multiChoice") {
      // For multiChoice: add/update custom text in the array while preserving selections
      setAnswers((prev) => {
        const currentAnswers = (prev[currentQuestion.id] as string[]) || [];
        // Remove previous custom text if it exists
        const withoutOldCustom = prevCustomText.trim()
          ? currentAnswers.filter((a) => a !== prevCustomText)
          : currentAnswers;
        // Add new custom text if not empty
        if (value.trim()) {
          return {
            ...prev,
            [currentQuestion.id]: [...withoutOldCustom, value],
          };
        }
        return { ...prev, [currentQuestion.id]: withoutOldCustom };
      });
    } else {
      // For choice/text: single value
      if (value.trim()) {
        setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
      }
    }
  };

  const isCustomAnswer = (() => {
    const custom = customText[currentQuestion?.id];
    if (!custom?.trim()) return false;
    const answer = answers[currentQuestion?.id];
    if (Array.isArray(answer)) {
      return answer.includes(custom);
    }
    return answer === custom;
  })();

  const isCurrentAnswered = (): boolean => {
    const answer = answers[currentQuestion?.id];
    if (!answer) return false;
    if (Array.isArray(answer)) return answer.length > 0;
    return answer.trim() !== "";
  };

  const canProceed = (): boolean => {
    if (!currentQuestion?.required) return true;
    return isCurrentAnswered();
  };

  const handleSubmit = (finalAnswers = answers) => {
    const formattedAnswers: UserAnswer[] = questions.map((q) => ({
      questionId: q.id,
      answer: finalAnswers[q.id] || (q.type === "multiChoice" ? [] : ""),
    }));
    onSubmit?.(formattedAnswers, false);
  };

  const handleSkip = () => onSubmit?.([], true);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  if (status === "failed" || status === "aborted" || wasSkipped) {
    return (
      <div className="text-xs text-secondary-n-500">Questions skipped</div>
    );
  }

  if (!questions || questions.length === 0 || !currentQuestion) {
    return null;
  }

  // Read-only summary view for historical questions (answered or not)
  if (!canSubmit) {
    const getAnswerForQuestion = (questionId: string): string => {
      const submitted = submittedAnswers?.find(
        (a) => a.questionId === questionId,
      );
      if (!submitted) return "";
      if (Array.isArray(submitted.answer)) {
        return submitted.answer.join(", ");
      }
      return submitted.answer;
    };

    // Get first answer as preview text
    const firstAnswer = getAnswerForQuestion(questions[0]?.id);
    const previewText = firstAnswer || "No response";

    return (
      <div className="my-2 bg-secondary-n-950 rounded-lg border border-[#2F3035] overflow-hidden">
        <Accordion type="single" collapsible defaultValue="">
          <AccordionItem value="user-input" className="border-none">
            <AccordionTrigger className="px-3 py-2 hover:no-underline group [&>svg]:hidden">
              <div className="flex items-center gap-2 w-full">
                <ChevronRightIcon className="h-3 w-3 text-text-tertiary transition-transform duration-200 group-data-[state=open]:rotate-90" />
                <span className="text-[10px] text-text-tertiary font-medium tracking-wider">
                  USER ANSWER
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-3">
              <div className="space-y-3">
                {questions.map((q) => {
                  const answer = getAnswerForQuestion(q.id);
                  return (
                    <div key={q.id} className="space-y-2">
                      <div className="border-l-2 border-secondary-n-700 pl-3">
                        <p className="text-xs text-text-secondary">
                          {q.question}
                        </p>
                      </div>
                      <p
                        className={cn(
                          "text-xs",
                          answer
                            ? "text-text-primary"
                            : "text-text-muted italic",
                        )}
                      >
                        {answer || "Not answered"}
                      </p>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  const ProgressHeader = () => (
    <div className="px-3 py-2 border-b border-[#2F3035] flex justify-between items-center">
      <div className="flex gap-1">
        {questions.map((_, index) => (
          <div
            key={index}
            className={cn(
              "w-5 h-0.5 rounded-sm transition-colors",
              index <= currentStep ? "bg-primary-0" : "bg-secondary-n-800",
            )}
          />
        ))}
      </div>
      <span className="text-[10px] text-text-secondary font-medium tracking-wider">
        STEP {currentStep + 1} OF {totalSteps}
      </span>
    </div>
  );

  const FooterNavigation = () => {
    const isReviewMode = hasSubmittedAnswers && !canSubmit;
    const nextDisabled = isReviewMode
      ? isNavigationDisabled
      : (currentQuestion?.required && !isCurrentAnswered()) ||
        isNavigationDisabled;

    const showNextButton =
      isReviewMode || currentQuestion?.type !== "choice" || isCustomAnswer;
    const showSkip = canSubmit;
    const showBack = currentStep > 0;
    const showNext = !isLastStep && showNextButton;
    const showSubmit = isLastStep && canSubmit;

    if (!showSkip && !showBack && !showNext && !showSubmit) {
      return null;
    }

    return (
      <div className="px-3 py-2 border-t border-[#2F3035] flex justify-between items-center bg-secondary-n-950/30">
        <div>
          {showSkip && (
            <button
              onClick={handleSkip}
              className="text-[10px] text-secondary-n-400 hover:text-secondary-n-300 transition-colors"
            >
              Skip
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showBack && (
            <button
              onClick={handleBack}
              disabled={isNavigationDisabled}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors",
                "border-secondary-n-800 bg-secondary-n-900 text-secondary-n-300",
                isNavigationDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-secondary-n-800 hover:border-secondary-n-700",
              )}
            >
              Back
            </button>
          )}

          {showSubmit ? (
            <button
              data-testid="question-submit-button"
              onClick={() => handleSubmit()}
              disabled={!canProceed() || isInputDisabled}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-sm transition-colors",
                "bg-primary-0 text-white",
                !canProceed() || isInputDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-primary-100",
              )}
            >
              {isSubmitting ? "Sent" : "Submit"}
            </button>
          ) : showNext ? (
            <button
              data-testid="question-next-button"
              onClick={handleNext}
              disabled={nextDisabled}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-sm transition-colors",
                "bg-primary-0 text-white",
                nextDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-primary-100",
              )}
            >
              Next
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderQuestionContent = () => (
    <div className="space-y-3">
      <div>
        <h3
          data-testid="question-text"
          className="text-xs font-medium text-text-primary leading-relaxed"
        >
          {currentQuestion?.question}
          {currentQuestion?.required && canSubmit && (
            <span className="text-red-400 ml-0.5">*</span>
          )}
        </h3>
        {currentQuestion?.type === "multiChoice" && (
          <p
            data-testid="question-multi-choice-hint"
            className="text-[10px] text-text-secondary mt-0.5"
          >
            Select all that apply
          </p>
        )}
      </div>

      {currentQuestion?.type === "text" && (
        <div className="relative">
          <input
            data-testid="question-text-input"
            type="text"
            placeholder={currentQuestion.placeholder || "Type your answer..."}
            value={(answers[currentQuestion.id] as string) || ""}
            onChange={(e) => handleTextChange(e.target.value)}
            onKeyDown={(e) => {
              const value = (answers[currentQuestion.id] as string) || "";
              if (e.key === "Enter" && value.trim()) {
                e.preventDefault();
                if (isLastStep) {
                  handleSubmit();
                } else {
                  handleNext();
                }
              }
            }}
            disabled={isInputDisabled}
            className={cn(
              "w-full px-2.5 py-2 pr-8 rounded-lg text-xs transition-colors outline-hidden",
              "bg-secondary-n-1000 border border-[#2F3035]",
              "text-text-primary placeholder:text-text-muted",
              "focus:border-primary-0",
              isInputDisabled && "opacity-60 cursor-not-allowed",
            )}
          />
          {!!((answers[currentQuestion.id] as string) || "").trim() &&
            !isInputDisabled && (
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted text-sm">
                ↵
              </span>
            )}
        </div>
      )}

      {currentQuestion?.type === "choice" && currentQuestion.options && (
        <div className="space-y-1.5">
          {currentQuestion.options.map((option, index) => (
            <OptionCard
              key={option}
              option={option}
              number={index + 1}
              isSelected={
                !isCustomAnswer &&
                (answers[currentQuestion.id] as string) === option
              }
              onClick={() => {
                const newAnswers = { ...answers, [currentQuestion.id]: option };
                setCustomText((prev) => ({
                  ...prev,
                  [currentQuestion.id]: "",
                }));
                setAnswers(newAnswers);

                if (isLastStep) {
                  setTimeout(() => handleSubmit(newAnswers), 150);
                } else if (!isInputDisabled) {
                  setTimeout(() => handleNext(), 150);
                }
              }}
              disabled={isInputDisabled}
            />
          ))}
          <CustomTextInput
            number={(currentQuestion.options?.length || 0) + 1}
            isCustomAnswer={isCustomAnswer}
            isInputDisabled={isInputDisabled}
            value={customText[currentQuestion.id] || ""}
            onChange={handleCustomTextChange}
            onEnterKey={() => {
              if (isLastStep) {
                handleSubmit();
              } else {
                handleNext();
              }
            }}
          />
        </div>
      )}

      {currentQuestion?.type === "multiChoice" && currentQuestion.options && (
        <div className="space-y-1.5">
          {currentQuestion.options.map((option, index) => {
            const currentAnswers =
              (answers[currentQuestion.id] as string[]) || [];
            const isChecked = currentAnswers.includes(option);
            return (
              <OptionCard
                key={option}
                option={option}
                number={index + 1}
                isSelected={isChecked}
                onClick={() => handleMultiChoiceChange(option)}
                disabled={isInputDisabled}
              />
            );
          })}
          <CustomTextInput
            number={(currentQuestion.options?.length || 0) + 1}
            isCustomAnswer={isCustomAnswer}
            isInputDisabled={isInputDisabled}
            value={customText[currentQuestion.id] || ""}
            onChange={handleCustomTextChange}
            onEnterKey={() => {
              if (isLastStep) {
                handleSubmit();
              } else {
                handleNext();
              }
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      data-testid="ask-user-questions-block"
      className="my-2 bg-secondary-n-950 border border-[#2F3035] rounded-lg overflow-hidden"
    >
      <ProgressHeader />

      <div className="p-3">{renderQuestionContent()}</div>

      <FooterNavigation />
    </div>
  );
};

export const ASK_USER_QUESTIONS_TOOL_NAME = "askUserQuestionsTool";
